
import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  DadosCliente, 
  DadosInstalacao, 
  KitSolar, 
  ParametrosFinanceiros, 
  PropostaComercial as PropostaComercialType,
  DadosIrradiacao
} from "@/types";
import { 
  FileText, 
  Download, 
  Send, 
  Sun, 
  Zap, 
  Home, 
  AreaChart, 
  BatteryCharging, 
  Workflow, 
  Leaf, 
  DollarSign, 
  BarChart3, 
  Timer, 
  CheckCircle2,
  ArrowRight,
  Calendar,
  LineChart,
  ArrowDownToLine,
  Smartphone,
  ChevronDown,
  LayoutDashboard,
  PieChart,
  FileImage
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { Separator } from "@/components/ui/separator";

interface PropostaComercialProps {
  dadosCliente: DadosCliente;
  dadosInstalacao: DadosInstalacao;
  dadosIrradiacao: DadosIrradiacao;
  kitSolar: KitSolar;
  parametrosFinanceiros: ParametrosFinanceiros;
}

export const PropostaComercial = ({
  dadosCliente,
  dadosInstalacao,
  dadosIrradiacao,
  kitSolar,
  parametrosFinanceiros,
}: PropostaComercialProps) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("resumo");
  const [viewMode, setViewMode] = useState<"tabs" | "onepage">("tabs");

  const calcularPrecoFinal = (): number => {
    const custoEquipamentos = kitSolar.precoTotal;
    const custoMaoDeObra = parametrosFinanceiros.custoMaoDeObra;
    const custoEngenharia = parametrosFinanceiros.custoEngenharia;
    const outrasDespesas = parametrosFinanceiros.outrasDespesas;
    
    const custoBase = custoEquipamentos + custoMaoDeObra + custoEngenharia + outrasDespesas;
    
    const precoComMargem = custoBase * (1 + parametrosFinanceiros.margemLucro / 100);
    
    const precoFinal = precoComMargem * (1 + parametrosFinanceiros.impostos / 100);
    
    return precoFinal;
  };

  const calcularEconomiaEstimada = (): number => {
    return kitSolar.geracaoEstimada * 0.9;
  };

  const calcularRetornoInvestimento = (): number => {
    return Math.round(calcularPrecoFinal() / calcularEconomiaEstimada());
  };

  const gerarProposta = (): PropostaComercialType => {
    return {
      id: `proposta-${Date.now()}`,
      cliente: dadosCliente,
      dataGeracao: new Date().toISOString(),
      kitSolar,
      parametrosFinanceiros,
      precoFinal: calcularPrecoFinal(),
      economiaEstimada: calcularEconomiaEstimada(),
      retornoInvestimento: calcularRetornoInvestimento(),
      criadoPor: user?.id || ""
    };
  };

  const handleExportarPDF = async () => {
    setLoading(true);
    try {
      const element = document.getElementById('proposta-comercial');
      if (!element) {
        throw new Error("Elemento não encontrado");
      }
      
      const canvas = await html2canvas(element, {
        scale: 2,
        logging: false,
        useCORS: true
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const imgWidth = 210; // A4 width
      const imgHeight = canvas.height * imgWidth / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      pdf.save(`proposta_${dadosCliente.nome.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
      
      toast.success("Proposta exportada com sucesso!");
    } catch (error) {
      console.error("Erro ao exportar proposta:", error);
      toast.error("Erro ao exportar proposta. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleEnviarProposta = () => {
    toast.info("Funcionalidade de envio ainda não implementada. Exporte o PDF.");
  };

  const toggleViewMode = () => {
    setViewMode(viewMode === "tabs" ? "onepage" : "tabs");
  };

  const precoFinal = calcularPrecoFinal();
  const economiaEstimada = calcularEconomiaEstimada();
  const retornoInvestimento = calcularRetornoInvestimento();
  const economiaAnual = economiaEstimada * 12;
  const economia25Anos = economiaEstimada * 12 * 25;

  // Dados para o gráfico de composição do sistema
  const composicaoSistema = [
    { name: 'Painéis', value: kitSolar.paineis.reduce((acc, p) => acc + (p.precoUnitario * p.quantidade), 0) },
    { name: 'Inversores', value: kitSolar.inversores.reduce((acc, i) => acc + (i.precoUnitario * i.quantidade), 0) },
    { name: 'Estruturas', value: kitSolar.estruturas.reduce((acc, e) => acc + (e.precoUnitario * e.quantidade), 0) },
    { name: 'Outros', value: kitSolar.outrosEquipamentos.reduce((acc, o) => acc + (o.precoUnitario * o.quantidade), 0) }
  ];

  // Dados para o gráfico de retorno do investimento
  const retornoInvestimentoData = Array.from({ length: 25 }, (_, i) => ({
    ano: i + 1,
    economia: economiaAnual * (i + 1),
    investimento: precoFinal
  }));

  return (
    <div className="mt-8" id="proposta-comercial">
      {/* Barra de opções de visualização */}
      <div className="flex justify-end mb-4 space-x-4">
        <Button 
          variant={viewMode === "tabs" ? "default" : "outline"} 
          size="sm"
          onClick={() => setViewMode("tabs")}
          className="flex items-center gap-2"
        >
          <Workflow className="h-4 w-4" />
          Visualização em Abas
        </Button>
        <Button 
          variant={viewMode === "onepage" ? "default" : "outline"} 
          size="sm"
          onClick={() => setViewMode("onepage")}
          className="flex items-center gap-2"
        >
          <LayoutDashboard className="h-4 w-4" />
          Visualização Única
        </Button>
      </div>

      {/* Cabeçalho com degradê azul e verde */}
      <div className="bg-gradient-to-r from-blue-800 via-blue-700 to-green-600 rounded-t-lg overflow-hidden">
        <div className="p-8 text-white">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold tracking-wide">Proposta Comercial</h1>
              <p className="text-white/80 mt-1">Sistema Fotovoltaico</p>
            </div>
            <img 
              src="/lovable-uploads/fd9a6526-d3cb-40d3-bf34-813d7de7a44d.png" 
              alt="Ecoefin Solar" 
              className="h-20 w-auto" 
            />
          </div>
          
          <div className="mt-12 text-center space-y-3 max-w-2xl mx-auto">
            <h2 className="text-4xl font-bold tracking-wide leading-tight">
              A <span className="text-green-400">Energia Solar</span> é a fonte de energia que mais cresce no mundo!
            </h2>
            <div className="bg-white text-blue-900 font-bold text-2xl py-2 px-6 rounded-lg inline-block mt-3">
              PRODUZA SUA PRÓPRIA ENERGIA!
            </div>
            <p className="text-xl mt-5 tracking-wide leading-relaxed">
              E economize em até <span className="text-yellow-400 font-bold text-2xl">95%</span> na sua conta de luz pelos próximos <span className="text-yellow-400 font-bold">25 anos</span>.
            </p>
          </div>
          
          <div className="flex justify-center mt-8">
            <ChevronDown className="h-8 w-8 animate-bounce" />
          </div>
        </div>
      </div>
      
      {/* Conteúdo principal - Modo Abas */}
      {viewMode === "tabs" && (
        <Card className="border-none shadow-lg mt-6">
          <CardHeader className="bg-gradient-to-r from-blue-100 to-green-100 dark:from-blue-900/30 dark:to-green-900/30">
            <div className="flex justify-between items-center">
              <CardTitle className="text-2xl text-blue-800 dark:text-blue-400">
                Detalhes da Proposta
              </CardTitle>
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <Calendar className="h-4 w-4" />
                Gerada em {new Date().toLocaleDateString()}
              </div>
            </div>
            <CardDescription className="text-base mt-1">
              Proposta personalizada para {dadosCliente.nome}
            </CardDescription>
          </CardHeader>
          
          <CardContent className="p-0">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <div className="px-6 pt-6 border-b">
                <TabsList className="grid grid-cols-2 md:grid-cols-5 gap-2 w-full h-auto p-1">
                  <TabsTrigger 
                    value="resumo" 
                    className="flex items-center gap-2 py-3 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700 dark:data-[state=active]:bg-blue-900/30"
                  >
                    <BarChart3 className="h-4 w-4" />
                    <span>Resumo</span>
                  </TabsTrigger>
                  <TabsTrigger 
                    value="beneficios" 
                    className="flex items-center gap-2 py-3 data-[state=active]:bg-green-50 data-[state=active]:text-green-700 dark:data-[state=active]:bg-green-900/30"
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    <span>Benefícios</span>
                  </TabsTrigger>
                  <TabsTrigger 
                    value="funcionamento" 
                    className="flex items-center gap-2 py-3 data-[state=active]:bg-yellow-50 data-[state=active]:text-yellow-700 dark:data-[state=active]:bg-yellow-900/30"
                  >
                    <Workflow className="h-4 w-4" />
                    <span>Como Funciona</span>
                  </TabsTrigger>
                  <TabsTrigger 
                    value="sistema" 
                    className="flex items-center gap-2 py-3 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700 dark:data-[state=active]:bg-blue-900/30"
                  >
                    <Sun className="h-4 w-4" />
                    <span>Sistema</span>
                  </TabsTrigger>
                  <TabsTrigger 
                    value="financeiro" 
                    className="flex items-center gap-2 py-3 data-[state=active]:bg-green-50 data-[state=active]:text-green-700 dark:data-[state=active]:bg-green-900/30"
                  >
                    <DollarSign className="h-4 w-4" />
                    <span>Financeiro</span>
                  </TabsTrigger>
                </TabsList>
              </div>
              
              {/* Aba de Resumo */}
              <TabsContent value="resumo" className="p-8 space-y-8">
                <div className="grid gap-8 md:grid-cols-2">
                  <Card className="border border-blue-200 shadow-sm">
                    <CardHeader className="bg-blue-50 dark:bg-blue-950/30 pb-4">
                      <CardTitle className="text-lg font-medium flex items-center gap-2 text-blue-800">
                        <Sun className="h-5 w-5 text-yellow-500" />
                        Dados do Cliente e Instalação
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-5 space-y-6">
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 mb-1">Cliente</h3>
                        <p className="font-medium text-lg">{dadosCliente.nome}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <h3 className="text-sm font-medium text-gray-500 mb-1">Consumo médio</h3>
                          <p className="font-medium text-lg text-blue-800">{dadosInstalacao.consumoMensal} kWh/mês</p>
                        </div>
                        <div>
                          <h3 className="text-sm font-medium text-gray-500 mb-1">Tipo de instalação</h3>
                          <p className="font-medium text-lg">{dadosInstalacao.tipologia.charAt(0).toUpperCase() + dadosInstalacao.tipologia.slice(1)}</p>
                        </div>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 mb-1">Localização</h3>
                        <p className="font-medium">{dadosIrradiacao.cep}</p>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 mb-1">Irradiação média</h3>
                        <p className="font-medium">{dadosIrradiacao.irradiacaoMedia.toFixed(2)} kWh/m²/dia</p>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border border-green-200 shadow-sm">
                    <CardHeader className="bg-green-50 dark:bg-green-950/30 pb-4">
                      <CardTitle className="text-lg font-medium flex items-center gap-2 text-green-800">
                        <BarChart3 className="h-5 w-5 text-green-600" />
                        Resultados Esperados
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-5 space-y-6">
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <h3 className="text-sm font-medium text-gray-500 mb-1">Potência do sistema</h3>
                          <p className="font-medium text-lg text-blue-800">{kitSolar.potenciaSistema.toFixed(2)} kWp</p>
                        </div>
                        <div>
                          <h3 className="text-sm font-medium text-gray-500 mb-1">Geração estimada</h3>
                          <p className="font-medium text-lg text-green-700">{kitSolar.geracaoEstimada} kWh/mês</p>
                        </div>
                      </div>
                      <div className="pt-1">
                        <h3 className="text-sm font-medium text-gray-500 mb-1">Economia mensal</h3>
                        <p className="text-xl font-bold text-green-600">
                          {economiaEstimada.toLocaleString("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          })}
                        </p>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 mb-1">Retorno do investimento</h3>
                        <p className="font-medium">{retornoInvestimento} meses <span className="text-sm text-gray-500">({(retornoInvestimento / 12).toFixed(1)} anos)</span></p>
                      </div>
                      <div className="bg-green-50 p-4 rounded-md border border-green-200">
                        <h3 className="text-sm font-medium text-green-800 mb-1">Economia em 25 anos</h3>
                        <p className="text-xl font-bold text-green-700">
                          {economia25Anos.toLocaleString("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          })}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
                
                <div className="bg-gradient-to-r from-blue-800 to-green-700 text-white p-8 rounded-lg shadow-md">
                  <div className="text-center space-y-4">
                    <h3 className="text-2xl font-bold">Investimento Total</h3>
                    <p className="text-4xl font-bold tracking-wide">
                      {precoFinal.toLocaleString("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      })}
                    </p>
                    <p className="text-base opacity-90 tracking-wide mt-2">
                      Potência total: {kitSolar.potenciaSistema.toFixed(2)} kWp | 
                      {" "}{kitSolar.paineis.reduce((total, painel) => total + painel.quantidade, 0)} painéis | 
                      {" "}{kitSolar.inversores.reduce((total, inversor) => total + inversor.quantidade, 0)} inversores
                    </p>
                    <div className="flex justify-center mt-4">
                      <Button variant="secondary" onClick={() => setActiveTab("financeiro")} size="lg" className="gap-2 text-lg">
                        Ver detalhes financeiros <ArrowRight className="h-5 w-5" />
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Gráfico de composição do sistema */}
                <Card className="border border-blue-200">
                  <CardHeader className="bg-blue-50 dark:bg-blue-950/30">
                    <CardTitle className="text-lg font-medium flex items-center gap-2 text-blue-800">
                      <PieChart className="h-5 w-5 text-blue-600" />
                      Composição do Investimento
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-5">
                    <div className="h-64 w-full">
                      {/* Aqui seria renderizado o gráfico com os dados de composicaoSistema */}
                      <div className="flex justify-center items-center h-full text-center">
                        <div className="grid grid-cols-4 gap-4 w-full">
                          {composicaoSistema.map((item, index) => (
                            <div key={index} className="flex flex-col items-center">
                              <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-2 
                                ${index === 0 ? 'bg-blue-500' : 
                                  index === 1 ? 'bg-green-500' : 
                                  index === 2 ? 'bg-yellow-500' : 'bg-purple-500'}`}>
                                <span className="text-white font-bold">
                                  {Math.round((item.value / precoFinal) * 100)}%
                                </span>
                              </div>
                              <span className="font-medium">{item.name}</span>
                              <span className="text-sm text-gray-600">
                                {item.value.toLocaleString("pt-BR", {
                                  style: "currency",
                                  currency: "BRL",
                                })}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
              
              {/* Aba de Benefícios */}
              <TabsContent value="beneficios" className="p-8 space-y-8">
                <div className="bg-blue-50 dark:bg-blue-950/30 p-8 rounded-lg text-center mb-8">
                  <h2 className="text-2xl font-bold text-blue-800 mb-3">
                    Seja <span className="text-green-600">eco</span> por um futuro melhor
                  </h2>
                  <p className="text-gray-600 max-w-2xl mx-auto text-lg leading-relaxed">
                    A energia solar não apenas economiza dinheiro, mas também contribui para um planeta mais sustentável.
                  </p>
                </div>
                
                <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                  <Card className="border-green-200 shadow-sm hover:shadow-md transition-shadow">
                    <CardHeader className="pb-4">
                      <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mb-3">
                        <Home className="h-7 w-7 text-green-600" />
                      </div>
                      <CardTitle className="text-lg">Valorização do imóvel</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600 leading-relaxed">
                        Imóveis com sistemas de energia solar têm valor de mercado mais alto e são mais atrativos para venda ou locação.
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-green-200 shadow-sm hover:shadow-md transition-shadow">
                    <CardHeader className="pb-4">
                      <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mb-3">
                        <AreaChart className="h-7 w-7 text-green-600" />
                      </div>
                      <CardTitle className="text-lg">Retorno do Investimento</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600 leading-relaxed">
                        Com retorno médio de {(retornoInvestimento / 12).toFixed(1)} anos, o investimento em energia solar é seguro e rentável.
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-green-200 shadow-sm hover:shadow-md transition-shadow">
                    <CardHeader className="pb-4">
                      <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mb-3">
                        <Leaf className="h-7 w-7 text-green-600" />
                      </div>
                      <CardTitle className="text-lg">Preserva o meio ambiente</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600 leading-relaxed">
                        Reduza sua pegada de carbono e contribua para um futuro mais sustentável com energia limpa e renovável.
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-green-200 shadow-sm hover:shadow-md transition-shadow">
                    <CardHeader className="pb-4">
                      <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mb-3">
                        <Sun className="h-7 w-7 text-yellow-500" />
                      </div>
                      <CardTitle className="text-lg">Fonte de energia inesgotável</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600 leading-relaxed">
                        A energia solar é abundante e estará disponível enquanto o sol existir, tornando-se uma fonte segura.
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-green-200 shadow-sm hover:shadow-md transition-shadow">
                    <CardHeader className="pb-4">
                      <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mb-3">
                        <DollarSign className="h-7 w-7 text-green-600" />
                      </div>
                      <CardTitle className="text-lg">Economia garantida</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600 leading-relaxed">
                        Reduza sua conta de luz em até 95%, com economia estimada de {" "}
                        {economiaEstimada.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })} por mês.
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-green-200 shadow-sm hover:shadow-md transition-shadow">
                    <CardHeader className="pb-4">
                      <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mb-3">
                        <Timer className="h-7 w-7 text-green-600" />
                      </div>
                      <CardTitle className="text-lg">Durabilidade e baixa manutenção</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600 leading-relaxed">
                        Sistemas fotovoltaicos têm vida útil de mais de 25 anos com manutenção mínima e garantias estendidas.
                      </p>
                    </CardContent>
                  </Card>
                </div>
                
                <div className="mt-8 bg-gradient-to-r from-blue-800 to-green-700 text-white p-8 rounded-lg text-center">
                  <h3 className="text-2xl font-bold mb-4">Impacto Ambiental Positivo</h3>
                  <div className="grid md:grid-cols-3 gap-6 mt-6">
                    <div className="bg-white/10 p-5 rounded-lg">
                      <h4 className="font-medium text-lg mb-2">Redução de CO₂ (anual)</h4>
                      <p className="text-3xl font-bold">
                        {(kitSolar.geracaoEstimada * 12 * 0.085 / 1000).toFixed(2)} toneladas
                      </p>
                    </div>
                    <div className="bg-white/10 p-5 rounded-lg">
                      <h4 className="font-medium text-lg mb-2">Equivalente em árvores</h4>
                      <p className="text-3xl font-bold">
                        {Math.round(kitSolar.geracaoEstimada * 12 * 0.085 / 1000 * 7)} árvores
                      </p>
                    </div>
                    <div className="bg-white/10 p-5 rounded-lg">
                      <h4 className="font-medium text-lg mb-2">Economia de água</h4>
                      <p className="text-3xl font-bold">
                        {Math.round(kitSolar.geracaoEstimada * 12 * 1.5)} litros
                      </p>
                    </div>
                  </div>
                </div>
              </TabsContent>
              
              {/* Aba de Como Funciona */}
              <TabsContent value="funcionamento" className="p-8 space-y-8">
                <h2 className="text-2xl font-bold text-blue-800 mb-6">Como Funciona um Sistema Solar?</h2>
                
                <div className="grid gap-6 md:grid-cols-4">
                  <div className="bg-blue-50 dark:bg-blue-950/30 p-6 rounded-lg text-center hover:shadow-md transition-shadow border border-blue-200">
                    <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-blue-700 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Sun className="h-10 w-10 text-white" />
                    </div>
                    <h3 className="font-medium mb-3 text-lg">1. Captação da Luz Solar</h3>
                    <p className="text-gray-600 leading-relaxed">
                      O painel solar recebe a luz do sol e produz energia elétrica.
                    </p>
                  </div>
                  
                  <div className="bg-blue-50 dark:bg-blue-950/30 p-6 rounded-lg text-center hover:shadow-md transition-shadow border border-blue-200">
                    <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-blue-700 rounded-full flex items-center justify-center mx-auto mb-4">
                      <BatteryCharging className="h-10 w-10 text-white" />
                    </div>
                    <h3 className="font-medium mb-3 text-lg">2. Conversão da Energia</h3>
                    <p className="text-gray-600 leading-relaxed">
                      O inversor converte a energia DC gerada em AC, utilizável em sua residência.
                    </p>
                  </div>
                  
                  <div className="bg-blue-50 dark:bg-blue-950/30 p-6 rounded-lg text-center hover:shadow-md transition-shadow border border-blue-200">
                    <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-blue-700 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Home className="h-10 w-10 text-white" />
                    </div>
                    <h3 className="font-medium mb-3 text-lg">3. Distribuição</h3>
                    <p className="text-gray-600 leading-relaxed">
                      A energia é distribuída para sua casa ou empresa, alimentando seus equipamentos.
                    </p>
                  </div>
                  
                  <div className="bg-blue-50 dark:bg-blue-950/30 p-6 rounded-lg text-center hover:shadow-md transition-shadow border border-blue-200">
                    <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-blue-700 rounded-full flex items-center justify-center mx-auto mb-4">
                      <ArrowDownToLine className="h-10 w-10 text-white" />
                    </div>
                    <h3 className="font-medium mb-3 text-lg">4. Compensação</h3>
                    <p className="text-gray-600 leading-relaxed">
                      O excesso de energia retorna à rede elétrica e gera créditos para uso futuro.
                    </p>
                  </div>
                </div>
                
                <Card className="border-blue-200 mt-8">
                  <CardHeader className="bg-blue-50 dark:bg-blue-950/30 pb-4">
                    <CardTitle className="text-xl">Nosso Cronograma de Instalação</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="relative">
                      <div className="absolute left-6 top-0 h-full w-1 bg-blue-200"></div>
                      <ol className="space-y-8">
                        <li className="relative pl-16">
                          <div className="absolute left-0 top-0 flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 border-2 border-blue-300 z-10">
                            <span className="text-blue-800 font-bold text-lg">1</span>
                          </div>
                          <h3 className="font-medium text-blue-800 text-lg mb-2">Dimensionamento da usina</h3>
                          <p className="text-gray-600 leading-relaxed">
                            Análise técnica, design do sistema e proposta inicial do projeto.
                          </p>
                        </li>
                        
                        <li className="relative pl-16">
                          <div className="absolute left-0 top-0 flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 border-2 border-blue-300 z-10">
                            <span className="text-blue-800 font-bold text-lg">2</span>
                          </div>
                          <h3 className="font-medium text-blue-800 text-lg mb-2">Conclusão da compra</h3>
                          <p className="text-gray-600 leading-relaxed">
                            Formalização do contrato e aquisição dos equipamentos necessários.
                          </p>
                        </li>
                        
                        <li className="relative pl-16">
                          <div className="absolute left-0 top-0 flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 border-2 border-blue-300 z-10">
                            <span className="text-blue-800 font-bold text-lg">3</span>
                          </div>
                          <h3 className="font-medium text-blue-800 text-lg mb-2">Instalação da usina solar</h3>
                          <p className="text-gray-600 leading-relaxed">
                            Montagem dos painéis, inversores e sistema de monitoramento.
                          </p>
                        </li>
                        
                        <li className="relative pl-16">
                          <div className="absolute left-0 top-0 flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 border-2 border-blue-300 z-10">
                            <span className="text-blue-800 font-bold text-lg">4</span>
                          </div>
                          <h3 className="font-medium text-blue-800 text-lg mb-2">Homologação na concessionária</h3>
                          <p className="text-gray-600 leading-relaxed">
                            Processo junto à distribuidora de energia para aprovar o sistema.
                          </p>
                        </li>
                        
                        <li className="relative pl-16">
                          <div className="absolute left-0 top-0 flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 border-2 border-blue-300 z-10">
                            <span className="text-blue-800 font-bold text-lg">5</span>
                          </div>
                          <h3 className="font-medium text-blue-800 text-lg mb-2">Conclusão do projeto</h3>
                          <p className="text-gray-600 leading-relaxed">
                            Ativação do sistema, comissionamento e testes de performance.
                          </p>
                        </li>
                      </ol>
                    </div>
                  </CardContent>
                </Card>
                
                <div className="mt-8 grid md:grid-cols-2 gap-8">
                  <Card className="border-blue-200">
                    <CardHeader className="bg-blue-50 dark:bg-blue-950/30 pb-4">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <FileText className="h-5 w-5 text-blue-600" />
                        Projeto e Regularização
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-5">
                      <p className="text-gray-600 leading-relaxed mb-4">
                        Valores incluem todos os custos com documentação e acompanhamento do processo junto à concessionária.
                      </p>
                      <ul className="space-y-3">
                        <li className="flex items-center gap-3">
                          <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                          <span className="leading-relaxed">Projeto técnico completo</span>
                        </li>
                        <li className="flex items-center gap-3">
                          <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                          <span className="leading-relaxed">Dimensionamento do sistema</span>
                        </li>
                        <li className="flex items-center gap-3">
                          <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                          <span className="leading-relaxed">Homologação junto à concessionária</span>
                        </li>
                      </ul>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-blue-200">
                    <CardHeader className="bg-blue-50 dark:bg-blue-950/30 pb-4">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Smartphone className="h-5 w-5 text-blue-600" />
                        Monitoramento em tempo real
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-5">
                      <p className="text-gray-600 leading-relaxed mb-4">
                        Além de todos os benefícios da energia solar, você pode contar com sistemas de monitoramento online para acompanhar o desempenho.
                      </p>
                      <ul className="space-y-3">
                        <li className="flex items-center gap-3">
                          <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                          <span className="leading-relaxed">Aplicativo para smartphone</span>
                        </li>
                        <li className="flex items-center gap-3">
                          <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                          <span className="leading-relaxed">Acompanhamento da geração em tempo real</span>
                        </li>
                        <li className="flex items-center gap-3">
                          <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                          <span className="leading-relaxed">Relatórios de economia e desempenho</span>
                        </li>
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
              
              {/* Aba de Sistema */}
              <TabsContent value="sistema" className="p-8 space-y-8">
                <div className="grid gap-8 md:grid-cols-2">
                  <div>
                    <h2 className="text-xl font-bold text-blue-800 mb-5">Dados da Instalação</h2>
                    <div className="grid grid-cols-2 gap-5">
                      <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950/30 dark:to-blue-900/30">
                        <CardContent className="p-5 text-center">
                          <div className="w-14 h-14 mx-auto bg-white rounded-full flex items-center justify-center shadow-sm mb-3">
                            <Zap className="h-7 w-7 text-blue-600" />
                          </div>
                          <h3 className="text-sm font-medium text-gray-600 mb-1">Consumo médio</h3>
                          <p className="text-2xl font-bold text-blue-800">{dadosInstalacao.consumoMensal} kWh</p>
                        </CardContent>
                      </Card>
                      
                      <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950/30 dark:to-blue-900/30">
                        <CardContent className="p-5 text-center">
                          <div className="w-14 h-14 mx-auto bg-white rounded-full flex items-center justify-center shadow-sm mb-3">
                            <DollarSign className="h-7 w-7 text-green-600" />
                          </div>
                          <h3 className="text-sm font-medium text-gray-600 mb-1">Custo Mensal Atual</h3>
                          <p className="text-2xl font-bold text-blue-800">
                            {(dadosInstalacao.consumoMensal * 0.9).toLocaleString("pt-BR", {
                              style: "currency",
                              currency: "BRL",
                            })}
                          </p>
                        </CardContent>
                      </Card>
                      
                      <Card className="border-blue-200 bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950/30 dark:to-green-900/30">
                        <CardContent className="p-5 text-center">
                          <div className="w-14 h-14 mx-auto bg-white rounded-full flex items-center justify-center shadow-sm mb-3">
                            <Home className="h-7 w-7 text-blue-800" />
                          </div>
                          <h3 className="text-sm font-medium text-gray-600 mb-1">Tipo de estrutura</h3>
                          <p className="text-lg font-bold text-blue-800">{dadosInstalacao.tipoTelhado || "Convencional"}</p>
                        </CardContent>
                      </Card>
                      
                      <Card className="border-blue-200 bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950/30 dark:to-green-900/30">
                        <CardContent className="p-5 text-center">
                          <div className="w-14 h-14 mx-auto bg-white rounded-full flex items-center justify-center shadow-sm mb-3">
                            <Sun className="h-7 w-7 text-yellow-500" />
                          </div>
                          <h3 className="text-sm font-medium text-gray-600 mb-1">Irradiação Solar</h3>
                          <p className="text-lg font-bold text-blue-800">{dadosIrradiacao.irradiacaoMedia.toFixed(2)} kWh/m²</p>
                        </CardContent>
                      </Card>
                    </div>
                    
                    <h2 className="text-xl font-bold text-blue-800 mt-8 mb-5">Parâmetros do Sistema</h2>
                    <div className="grid grid-cols-2 gap-5">
                      <Card className="border-green-200 bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950/30 dark:to-green-900/30">
                        <CardContent className="p-5 text-center">
                          <h3 className="text-sm font-medium text-gray-600 mb-1">Potência do Sistema</h3>
                          <p className="text-2xl font-bold text-green-700">{kitSolar.potenciaSistema.toFixed(2)} kWp</p>
                        </CardContent>
                      </Card>
                      
                      <Card className="border-green-200 bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950/30 dark:to-green-900/30">
                        <CardContent className="p-5 text-center">
                          <h3 className="text-sm font-medium text-gray-600 mb-1">Área Utilizada</h3>
                          <p className="text-2xl font-bold text-green-700">
                            {Math.ceil(kitSolar.potenciaSistema * 6)} m²
                          </p>
                        </CardContent>
                      </Card>
                      
                      <Card className="border-green-200 bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950/30 dark:to-green-900/30">
                        <CardContent className="p-5 text-center">
                          <h3 className="text-sm font-medium text-gray-600 mb-1">Geração Estimada</h3>
                          <p className="text-2xl font-bold text-green-700">{kitSolar.geracaoEstimada} kWh/mês</p>
                        </CardContent>
                      </Card>
                      
                      <Card className="border-green-200 bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950/30 dark:to-green-900/30">
                        <CardContent className="p-5 text-center">
                          <h3 className="text-sm font-medium text-gray-600 mb-1">Economia Mensal</h3>
                          <p className="text-2xl font-bold text-green-700">
                            {economiaEstimada.toLocaleString("pt-BR", {
                              style: "currency",
                              currency: "BRL",
                            })}
                          </p>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                  
                  <div>
                    <h2 className="text-xl font-bold text-blue-800 mb-5">Topologia do Sistema</h2>
                    
                    <Card className="border-blue-200 mb-6">
                      <CardHeader className="bg-blue-50 dark:bg-blue-950/30 pb-4">
                        <CardTitle className="text-lg">Módulos Fotovoltaicos</CardTitle>
                      </CardHeader>
                      <CardContent className="pt-5">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="font-medium">Modelo</TableHead>
                              <TableHead className="font-medium">Potência</TableHead>
                              <TableHead className="text-right font-medium">Quantidade</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {kitSolar.paineis.map((painel) => (
                              <TableRow key={painel.id}>
                                <TableCell className="font-medium">
                                  {painel.marca} {painel.modelo}
                                </TableCell>
                                <TableCell>{painel.potencia} W</TableCell>
                                <TableCell className="text-right">{painel.quantidade} un</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-blue-200 mb-6">
                      <CardHeader className="bg-blue-50 dark:bg-blue-950/30 pb-4">
                        <CardTitle className="text-lg">Inversores</CardTitle>
                      </CardHeader>
                      <CardContent className="pt-5">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="font-medium">Modelo</TableHead>
                              <TableHead className="font-medium">Potência</TableHead>
                              <TableHead className="text-right font-medium">Quantidade</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {kitSolar.inversores.map((inversor) => (
                              <TableRow key={inversor.id}>
                                <TableCell className="font-medium">
                                  {inversor.marca} {inversor.modelo}
                                </TableCell>
                                <TableCell>{inversor.potencia} kW</TableCell>
                                <TableCell className="text-right">{inversor.quantidade} un</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-blue-200">
                      <CardHeader className="bg-blue-50 dark:bg-blue-950/30 pb-4">
                        <CardTitle className="text-lg">Estruturas e Outros Equipamentos</CardTitle>
                      </CardHeader>
                      <CardContent className="pt-5">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="font-medium">Descrição</TableHead>
                              <TableHead className="text-right font-medium">Quantidade</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {kitSolar.estruturas.concat(kitSolar.outrosEquipamentos).map((item) => (
                              <TableRow key={item.id}>
                                <TableCell className="font-medium">
                                  {item.marca} {item.modelo}
                                </TableCell>
                                <TableCell className="text-right">{item.quantidade} un</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </TabsContent>
              
              {/* Aba Financeiro */}
              <TabsContent value="financeiro" className="p-8 space-y-8">
                <h2 className="text-2xl font-bold text-blue-800 mb-6">Análise Financeira de Investimento</h2>
                
                <div className="grid gap-8 md:grid-cols-3">
                  <Card className="border-green-200 shadow-md">
                    <CardHeader className="bg-gradient-to-r from-green-500 to-green-700 text-white pb-4">
                      <CardTitle className="text-lg tracking-wide">Investimento Total</CardTitle>
                    </CardHeader>
                    <CardContent className="p-6 text-center">
                      <p className="text-3xl font-bold text-green-700">
                        {precoFinal.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </p>
                      <p className="text-sm text-gray-500 mt-3 leading-relaxed">
                        Valor total do projeto incluindo equipamentos, instalação e homologação
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-green-200 shadow-md">
                    <CardHeader className="bg-gradient-to-r from-blue-500 to-blue-700 text-white pb-4">
                      <CardTitle className="text-lg tracking-wide">Economia Anual</CardTitle>
                    </CardHeader>
                    <CardContent className="p-6 text-center">
                      <p className="text-3xl font-bold text-blue-700">
                        {economiaAnual.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </p>
                      <p className="text-sm text-gray-500 mt-3 leading-relaxed">
                        Valor economizado anualmente na conta de energia
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-green-200 shadow-md">
                    <CardHeader className="bg-gradient-to-r from-purple-500 to-purple-700 text-white pb-4">
                      <CardTitle className="text-lg tracking-wide">Economia em 25 anos</CardTitle>
                    </CardHeader>
                    <CardContent className="p-6 text-center">
                      <p className="text-3xl font-bold text-purple-700">
                        {economia25Anos.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </p>
                      <p className="text-sm text-gray-500 mt-3 leading-relaxed">
                        Projeção de economia durante a vida útil do sistema
                      </p>
                    </CardContent>
                  </Card>
                </div>
                
                <Card className="border-blue-200 mt-8">
                  <CardHeader className="bg-blue-50 dark:bg-blue-950/30 pb-4">
                    <CardTitle className="text-lg">Detalhamento dos Custos</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-5">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="font-medium w-1/3">Item</TableHead>
                          <TableHead className="font-medium w-1/3">Descrição</TableHead>
                          <TableHead className="text-right font-medium w-1/3">Valor</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        <TableRow>
                          <TableCell className="font-medium">Equipamentos</TableCell>
                          <TableCell>Painéis, inversores e estruturas</TableCell>
                          <TableCell className="text-right">
                            {kitSolar.precoTotal.toLocaleString("pt-BR", {
                              style: "currency",
                              currency: "BRL",
                            })}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-medium">Mão de Obra</TableCell>
                          <TableCell>Instalação e configuração</TableCell>
                          <TableCell className="text-right">
                            {parametrosFinanceiros.custoMaoDeObra.toLocaleString("pt-BR", {
                              style: "currency",
                              currency: "BRL",
                            })}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-medium">Projeto e Homologação</TableCell>
                          <TableCell>Engenharia e documentação</TableCell>
                          <TableCell className="text-right">
                            {parametrosFinanceiros.custoEngenharia.toLocaleString("pt-BR", {
                              style: "currency",
                              currency: "BRL",
                            })}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-medium">Outras Despesas</TableCell>
                          <TableCell>Suportes, cabos e acessórios</TableCell>
                          <TableCell className="text-right">
                            {parametrosFinanceiros.outrasDespesas.toLocaleString("pt-BR", {
                              style: "currency",
                              currency: "BRL",
                            })}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-medium">Subtotal</TableCell>
                          <TableCell></TableCell>
                          <TableCell className="text-right font-medium">
                            {(
                              kitSolar.precoTotal +
                              parametrosFinanceiros.custoMaoDeObra +
                              parametrosFinanceiros.custoEngenharia +
                              parametrosFinanceiros.outrasDespesas
                            ).toLocaleString("pt-BR", {
                              style: "currency",
                              currency: "BRL",
                            })}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-medium">Impostos e Margem</TableCell>
                          <TableCell>Impostos ({parametrosFinanceiros.impostos}%) e Margem ({parametrosFinanceiros.margemLucro}%)</TableCell>
                          <TableCell className="text-right">
                            {(precoFinal - (
                              kitSolar.precoTotal +
                              parametrosFinanceiros.custoMaoDeObra +
                              parametrosFinanceiros.custoEngenharia +
                              parametrosFinanceiros.outrasDespesas
                            )).toLocaleString("pt-BR", {
                              style: "currency",
                              currency: "BRL",
                            })}
                          </TableCell>
                        </TableRow>
                        <TableRow className="bg-blue-50 dark:bg-blue-950/30">
                          <TableCell className="font-bold text-lg">Total</TableCell>
                          <TableCell></TableCell>
                          <TableCell className="text-right font-bold text-lg">
                            {precoFinal.toLocaleString("pt-BR", {
                              style: "currency",
                              currency: "BRL",
                            })}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
                
                <div className="mt-8 grid gap-8 md:grid-cols-2">
                  <Card className="border-green-200">
                    <CardHeader className="bg-green-50 dark:bg-green-950/30 pb-4">
                      <CardTitle className="text-lg">Análise de Viabilidade</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-5">
                      <div className="space-y-6">
                        <div className="flex justify-between items-center">
                          <span className="font-medium">Tempo de Retorno</span>
                          <span className="text-xl font-bold">
                            {retornoInvestimento} meses ({(retornoInvestimento / 12).toFixed(1)} anos)
                          </span>
                        </div>
                        <Separator />
                        <div className="flex justify-between items-center">
                          <span className="font-medium">Taxa Interna de Retorno (TIR)</span>
                          <span className="text-xl font-bold text-green-600">
                            {Math.round(100 / (retornoInvestimento / 12))}% a.a.
                          </span>
                        </div>
                        <Separator />
                        <div className="flex justify-between items-center">
                          <span className="font-medium">Economia Mensal</span>
                          <span className="text-xl font-bold text-green-600">
                            {economiaEstimada.toLocaleString("pt-BR", {
                              style: "currency",
                              currency: "BRL",
                            })}
                          </span>
                        </div>
                        <Separator />
                        <div className="flex justify-between items-center">
                          <span className="font-medium">Valor Presente Líquido (VPL)</span>
                          <span className="text-xl font-bold text-green-600">
                            {(economia25Anos - precoFinal).toLocaleString("pt-BR", {
                              style: "currency",
                              currency: "BRL",
                            })}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-blue-200">
                    <CardHeader className="bg-blue-50 dark:bg-blue-950/30 pb-4">
                      <CardTitle className="text-lg">Comparativo de Investimentos</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-5">
                      <div className="space-y-6">
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-3">
                            <Sun className="h-6 w-6 text-yellow-500" />
                            <span className="font-medium">Energia Solar</span>
                          </div>
                          <span className="text-lg font-bold text-green-600">
                            {Math.round(100 / (retornoInvestimento / 12))}% a.a.
                          </span>
                        </div>
                        <Separator />
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-3">
                            <LineChart className="h-6 w-6 text-blue-600" />
                            <span className="font-medium">Poupança</span>
                          </div>
                          <span className="text-lg font-bold">
                            6% a.a.
                          </span>
                        </div>
                        <Separator />
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-3">
                            <DollarSign className="h-6 w-6 text-green-600" />
                            <span className="font-medium">Tesouro Direto</span>
                          </div>
                          <span className="text-lg font-bold">
                            9% a.a.
                          </span>
                        </div>
                        <Separator />
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-3">
                            <Home className="h-6 w-6 text-blue-800" />
                            <span className="font-medium">Imóveis</span>
                          </div>
                          <span className="text-lg font-bold">
                            8% a.a.
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
          <CardFooter className="flex flex-col sm:flex-row gap-4 justify-end border-t bg-gray-50 dark:bg-gray-800/30 py-5 px-8">
            <Button variant="outline" onClick={handleEnviarProposta} className="w-full sm:w-auto" disabled={loading}>
              <Send className="mr-2 h-4 w-4" />
              Enviar por E-mail
            </Button>
            <Button onClick={handleExportarPDF} className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700" disabled={loading}>
              {loading ? (
                <>
                  <FileText className="mr-2 h-4 w-4 animate-pulse" />
                  Gerando PDF...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Exportar PDF
                </>
              )}
            </Button>
          </CardFooter>
        </Card>
      )}
      
      {/* Visualização única - Modo One-Page */}
      {viewMode === "onepage" && (
        <Card className="border-none shadow-lg mt-6">
          <CardHeader className="bg-gradient-to-r from-blue-100 to-green-100 dark:from-blue-900/30 dark:to-green-900/30">
            <div className="flex justify-between items-center">
              <CardTitle className="text-2xl text-blue-800 dark:text-blue-400">
                Proposta Comercial Completa
              </CardTitle>
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <Calendar className="h-4 w-4" />
                Gerada em {new Date().toLocaleDateString()}
              </div>
            </div>
            <CardDescription className="text-base mt-1">
              Cliente: {dadosCliente.nome} | Potência: {kitSolar.potenciaSistema.toFixed(2)} kWp | Valor: {precoFinal.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
            </CardDescription>
          </CardHeader>
          
          <CardContent className="p-8 space-y-10">
            {/* Seção de Resumo */}
            <section className="space-y-6">
              <h2 className="text-2xl font-bold text-blue-800 flex items-center gap-2 border-b pb-2">
                <BarChart3 className="h-6 w-6 text-blue-600" />
                Resumo da Proposta
              </h2>
              
              <div className="grid gap-6 md:grid-cols-3">
                <Card className="border-blue-200 bg-blue-50/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Dados do Cliente</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Cliente</h3>
                      <p className="font-medium">{dadosCliente.nome}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Consumo médio</h3>
                      <p className="font-medium text-blue-800">{dadosInstalacao.consumoMensal} kWh/mês</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Tipo de instalação</h3>
                      <p className="font-medium">{dadosInstalacao.tipologia.charAt(0).toUpperCase() + dadosInstalacao.tipologia.slice(1)}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Localização</h3>
                      <p className="font-medium">{dadosIrradiacao.cep}</p>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border-green-200 bg-green-50/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Sistema Solar</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Potência do sistema</h3>
                      <p className="font-medium text-blue-800">{kitSolar.potenciaSistema.toFixed(2)} kWp</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Geração estimada</h3>
                      <p className="font-medium text-green-700">{kitSolar.geracaoEstimada} kWh/mês</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Área utilizada</h3>
                      <p className="font-medium">{Math.ceil(kitSolar.potenciaSistema * 6)} m²</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Módulos</h3>
                      <p className="font-medium">{kitSolar.paineis.reduce((total, painel) => total + painel.quantidade, 0)} painéis</p>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border-blue-200 bg-blue-50/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Financeiro</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Investimento total</h3>
                      <p className="font-medium text-blue-800">
                        {precoFinal.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Economia mensal</h3>
                      <p className="font-medium text-green-700">
                        {economiaEstimada.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Retorno do investimento</h3>
                      <p className="font-medium">{retornoInvestimento} meses ({(retornoInvestimento / 12).toFixed(1)} anos)</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">TIR</h3>
                      <p className="font-medium">{Math.round(100 / (retornoInvestimento / 12))}% a.a.</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              <div className="bg-gradient-to-r from-blue-800 to-green-700 text-white p-6 rounded-lg shadow-md">
                <div className="text-center space-y-3">
                  <h3 className="text-xl font-bold">Economia em 25 anos</h3>
                  <p className="text-3xl font-bold">
                    {economia25Anos.toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    })}
                  </p>
                </div>
              </div>
            </section>
            
            {/* Seção de Equipamentos */}
            <section className="space-y-6">
              <h2 className="text-2xl font-bold text-blue-800 flex items-center gap-2 border-b pb-2">
                <Sun className="h-6 w-6 text-yellow-500" />
                Equipamentos e Especificações
              </h2>
              
              <div className="grid gap-6 md:grid-cols-2">
                <Card className="border-blue-200">
                  <CardHeader className="bg-blue-50 dark:bg-blue-950/30 pb-3">
                    <CardTitle className="text-lg">Módulos Fotovoltaicos</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Modelo</TableHead>
                          <TableHead>Potência</TableHead>
                          <TableHead className="text-right">Quantidade</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {kitSolar.paineis.map((painel) => (
                          <TableRow key={painel.id}>
                            <TableCell className="font-medium">
                              {painel.marca} {painel.modelo}
                            </TableCell>
                            <TableCell>{painel.potencia} W</TableCell>
                            <TableCell className="text-right">{painel.quantidade} un</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
                
                <Card className="border-blue-200">
                  <CardHeader className="bg-blue-50 dark:bg-blue-950/30 pb-3">
                    <CardTitle className="text-lg">Inversores</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Modelo</TableHead>
                          <TableHead>Potência</TableHead>
                          <TableHead className="text-right">Quantidade</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {kitSolar.inversores.map((inversor) => (
                          <TableRow key={inversor.id}>
                            <TableCell className="font-medium">
                              {inversor.marca} {inversor.modelo}
                            </TableCell>
                            <TableCell>{inversor.potencia} kW</TableCell>
                            <TableCell className="text-right">{inversor.quantidade} un</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </div>
              
              <Card className="border-blue-200">
                <CardHeader className="bg-blue-50 dark:bg-blue-950/30 pb-3">
                  <CardTitle className="text-lg">Estruturas e Outros Equipamentos</CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Descrição</TableHead>
                        <TableHead className="text-right">Quantidade</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {kitSolar.estruturas.concat(kitSolar.outrosEquipamentos).map((item) => (
                        <TableRow key={item.id}>
                          <TableCell className="font-medium">
                            {item.marca} {item.modelo}
                          </TableCell>
                          <TableCell className="text-right">{item.quantidade} un</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </section>
            
            {/* Seção Financeira */}
            <section className="space-y-6">
              <h2 className="text-2xl font-bold text-blue-800 flex items-center gap-2 border-b pb-2">
                <DollarSign className="h-6 w-6 text-green-600" />
                Análise Financeira
              </h2>
              
              <div className="grid gap-6 md:grid-cols-3">
                <Card className="border-green-200 shadow-md">
                  <CardHeader className="bg-gradient-to-r from-green-500 to-green-700 text-white pb-3">
                    <CardTitle className="text-lg">Investimento Total</CardTitle>
                  </CardHeader>
                  <CardContent className="p-5 text-center">
                    <p className="text-3xl font-bold text-green-700">
                      {precoFinal.toLocaleString("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      })}
                    </p>
                  </CardContent>
                </Card>
                
                <Card className="border-green-200 shadow-md">
                  <CardHeader className="bg-gradient-to-r from-blue-500 to-blue-700 text-white pb-3">
                    <CardTitle className="text-lg">Economia Anual</CardTitle>
                  </CardHeader>
                  <CardContent className="p-5 text-center">
                    <p className="text-3xl font-bold text-blue-700">
                      {economiaAnual.toLocaleString("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      })}
                    </p>
                  </CardContent>
                </Card>
                
                <Card className="border-green-200 shadow-md">
                  <CardHeader className="bg-gradient-to-r from-purple-500 to-purple-700 text-white pb-3">
                    <CardTitle className="text-lg">ROI</CardTitle>
                  </CardHeader>
                  <CardContent className="p-5 text-center">
                    <p className="text-3xl font-bold text-purple-700">
                      {Math.round(100 / (retornoInvestimento / 12))}% a.a.
                    </p>
                  </CardContent>
                </Card>
              </div>
              
              <Card className="border-blue-200">
                <CardHeader className="bg-blue-50 dark:bg-blue-950/30 pb-3">
                  <CardTitle className="text-lg">Detalhamento dos Custos</CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item</TableHead>
                        <TableHead className="text-right">Valor</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell className="font-medium">Equipamentos</TableCell>
                        <TableCell className="text-right">
                          {kitSolar.precoTotal.toLocaleString("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          })}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Mão de Obra</TableCell>
                        <TableCell className="text-right">
                          {parametrosFinanceiros.custoMaoDeObra.toLocaleString("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          })}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Projeto e Homologação</TableCell>
                        <TableCell className="text-right">
                          {parametrosFinanceiros.custoEngenharia.toLocaleString("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          })}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Outras Despesas</TableCell>
                        <TableCell className="text-right">
                          {parametrosFinanceiros.outrasDespesas.toLocaleString("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          })}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Impostos e Margem</TableCell>
                        <TableCell className="text-right">
                          {(precoFinal - (
                            kitSolar.precoTotal +
                            parametrosFinanceiros.custoMaoDeObra +
                            parametrosFinanceiros.custoEngenharia +
                            parametrosFinanceiros.outrasDespesas
                          )).toLocaleString("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          })}
                        </TableCell>
                      </TableRow>
                      <TableRow className="bg-blue-50 dark:bg-blue-950/30">
                        <TableCell className="font-bold text-lg">Total</TableCell>
                        <TableCell className="text-right font-bold text-lg">
                          {precoFinal.toLocaleString("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          })}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </section>
            
            {/* Seção de Benefícios */}
            <section className="space-y-6">
              <h2 className="text-2xl font-bold text-blue-800 flex items-center gap-2 border-b pb-2">
                <CheckCircle2 className="h-6 w-6 text-green-600" />
                Benefícios e Vantagens
              </h2>
              
              <div className="grid gap-6 md:grid-cols-3">
                <div className="bg-green-50 p-5 rounded-lg border border-green-200 flex flex-col items-center text-center">
                  <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mb-3">
                    <Home className="h-7 w-7 text-green-600" />
                  </div>
                  <h3 className="font-medium text-lg mb-2">Valorização do imóvel</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Imóveis com sistemas solares têm maior valor de mercado.
                  </p>
                </div>
                
                <div className="bg-green-50 p-5 rounded-lg border border-green-200 flex flex-col items-center text-center">
                  <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mb-3">
                    <Leaf className="h-7 w-7 text-green-600" />
                  </div>
                  <h3 className="font-medium text-lg mb-2">Benefício ambiental</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Reduza sua pegada de carbono com energia limpa.
                  </p>
                </div>
                
                <div className="bg-green-50 p-5 rounded-lg border border-green-200 flex flex-col items-center text-center">
                  <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mb-3">
                    <DollarSign className="h-7 w-7 text-green-600" />
                  </div>
                  <h3 className="font-medium text-lg mb-2">Economia garantida</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Reduza sua conta de luz em até 95%.
                  </p>
                </div>
              </div>
              
              <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                <h3 className="font-bold text-blue-800 text-xl mb-4 text-center">Impacto Ambiental Positivo</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="text-center">
                    <h4 className="font-medium">Redução de CO₂ (anual)</h4>
                    <p className="text-xl font-bold text-green-700">
                      {(kitSolar.geracaoEstimada * 12 * 0.085 / 1000).toFixed(2)} toneladas
                    </p>
                  </div>
                  <div className="text-center">
                    <h4 className="font-medium">Equivalente em árvores</h4>
                    <p className="text-xl font-bold text-green-700">
                      {Math.round(kitSolar.geracaoEstimada * 12 * 0.085 / 1000 * 7)} árvores
                    </p>
                  </div>
                  <div className="text-center">
                    <h4 className="font-medium">Economia de água</h4>
                    <p className="text-xl font-bold text-green-700">
                      {Math.round(kitSolar.geracaoEstimada * 12 * 1.5)} litros
                    </p>
                  </div>
                </div>
              </div>
            </section>
          </CardContent>
          
          <CardFooter className="flex flex-col sm:flex-row gap-4 justify-end border-t bg-gray-50 dark:bg-gray-800/30 py-5 px-8">
            <Button variant="outline" onClick={handleEnviarProposta} className="w-full sm:w-auto" disabled={loading}>
              <Send className="mr-2 h-4 w-4" />
              Enviar por E-mail
            </Button>
            <Button onClick={handleExportarPDF} className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700" disabled={loading}>
              {loading ? (
                <>
                  <FileText className="mr-2 h-4 w-4 animate-pulse" />
                  Gerando PDF...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Exportar PDF
                </>
              )}
            </Button>
          </CardFooter>
        </Card>
      )}
      
      {/* Rodapé */}
      <div className="mt-8 p-6 bg-gradient-to-r from-blue-800 to-blue-900 text-white rounded-b-lg">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-xl font-bold mb-4">Nós cuidamos de tudo para você!</h2>
          <div className="grid grid-cols-3 gap-6 mb-6">
            <div className="text-center">
              <p className="font-medium">Presente em todo Brasil</p>
            </div>
            <div className="text-center">
              <p className="font-medium">+1000 instalações realizadas</p>
            </div>
            <div className="text-center">
              <p className="font-medium">+3MW de potência instalada</p>
            </div>
          </div>
          <p className="text-sm opacity-80 mt-6 leading-relaxed">
            E então, o que achou? São mesmo impressionantes os benefícios de gerar energia de forma gratuita e limpa. 
            Agora é com você! Estamos prontos para ouvir suas dúvidas e opiniões.
          </p>
          <div className="mt-6 grid grid-cols-3 gap-2 text-sm opacity-80 border-t border-white/20 pt-6">
            <div>Aprovado pelo Cliente</div>
            <div>Ecoefin Energia Solar</div>
            <div>Data: {new Date().toLocaleDateString()}</div>
          </div>
        </div>
      </div>
    </div>
  );
};
